import {
    DeleteOutlined,
    DownOutlined,
    EditOutlined,
    EyeOutlined,
    MoreOutlined,
    SendOutlined,
    ShareAltOutlined,
    UpOutlined,
    WalletOutlined
} from '@ant-design/icons'
import type { Post } from '@entities/post'
import { AdPriceMethod } from '@entities/post/model/types'
import { classNames } from '@shared/lib/classNames'
import { dateToShortString } from '@shared/lib/dateToShortString/dateToShortString'
import { dateToTimeString } from '@shared/lib/dateToTimeString/dateToTimeString'
import { App, Avatar, Button, Col, Dropdown, Flex, Row, Space, theme, Typography } from 'antd'
import { Dialog } from 'antd-mobile'
import { useMemo, useState } from 'react'
import { Share } from 'react-feather'
import * as styles from './ad-mobile-post.module.scss'

const { Text } = Typography

interface AdMobilePostProps {
    post: Post
    onEdit: (post: Post) => void
    onPreview: (post: Post) => void
    onPreviewToBot: (post: Post) => void
    onDelete: () => void
}

export const AdMobilePost = ({ post, onEdit, onPreviewToBot, onPreview, onDelete }: AdMobilePostProps) => {
    const [collapsed, setCollapsed] = useState(false)

    const {
        token: { colorPrimary, colorLink, colorError, colorSuccess }
    } = theme.useToken()

    const isPaid = post.advertisingInfo?.paymentConfirmed
    const isFix = post.advertisingInfo?.priceMethod === AdPriceMethod.fix

    const cpsCommmon =
        post.advertisingInfo?.budget &&
        post.advertisingInfo?.statistics.joins &&
        post.advertisingInfo?.budget / post.advertisingInfo?.statistics.joins

    const cpsPure =
        post.advertisingInfo?.budget &&
        post.advertisingInfo?.statistics.joins &&
        post.advertisingInfo?.budget /
            (post.advertisingInfo?.statistics.joins - post.advertisingInfo?.statistics.leaves)

    const onShareStatistics = () => {}

    const { message } = App.useApp()

    const moreActionsItems = useMemo(
        () => [
            {
                key: '0',
                label: (
                    <Space style={{ color: colorLink }}>
                        <EyeOutlined /> Превью поста
                    </Space>
                ),
                onClick: () => onPreview(post)
            },
            {
                key: '1',
                label: (
                    <Space style={{ color: colorLink, width: '100%' }}>
                        <EditOutlined /> Редактировать
                    </Space>
                ),
                disabled: post.advertisingInfo?.customerConfirm && post.advertisingInfo.sellerConfirm,
                onClick: () => onEdit(post)
            },
            {
                key: '2',
                label: (
                    <Space style={{ color: colorLink, width: '100%' }}>
                        <EyeOutlined /> Предпросмотр в бот
                    </Space>
                ),
                onClick: () => onPreviewToBot(post)
            },
            {
                key: '3',
                label: (
                    <Space style={{ color: colorLink, width: '100%' }}>
                        <ShareAltOutlined /> Ссылка на договор
                    </Space>
                ),
                onClick: () =>
                    navigator.clipboard
                        .writeText(
                            `${window.location.origin}/api/partners/contract/${post.advertisingInfo?.contractUuid}`
                        )
                        .then(() => {
                            message.success('Скопировано в буфер обмена')
                        })
            },
            {
                key: '4',
                label: (
                    <Space style={{ color: colorLink, width: '100%' }}>
                        <SendOutlined /> Отправить Договор себе в PDF
                    </Space>
                ),
                onClick: () => {}
            },
            {
                key: '5',
                danger: true,
                label: (
                    <Space style={{ width: '100%' }}>
                        <DeleteOutlined /> Удалить
                    </Space>
                ),
                onClick: () =>
                    Dialog.confirm({
                        content: 'Вы уверены что хотите удалить пост?',
                        cancelText: 'Отмена',
                        confirmText: 'Удалить',
                        onConfirm: onDelete
                    })
            }
        ],
        [colorLink, message, onDelete, onEdit, onPreview, onPreviewToBot, post]
    )

    return (
        <div className={styles.container}>
            <Button type="text" className={styles.expandBtn} onClick={() => setCollapsed((prev) => !prev)}>
                {collapsed ? (
                    <UpOutlined style={{ color: colorPrimary }} />
                ) : (
                    <DownOutlined style={{ color: colorPrimary }} />
                )}
            </Button>
            <Flex gap={8}>
                <Avatar src="" size={68} />
                <Flex gap={4} vertical flex={1}>
                    <Flex align="center" justify="space-between">
                        <Text style={{ color: colorPrimary }}>{post.advertisingInfo?.internetResource.name}</Text>
                    </Flex>

                    <Flex justify="space-between">
                        <Text style={{ color: colorPrimary }}>
                            {post.publishDate && dateToShortString(post.publishDate)}
                        </Text>
                        <Text style={{ color: colorPrimary }}>
                            {post.publishDate && dateToTimeString(post.publishDate)}
                        </Text>
                        <Text style={{ color: colorPrimary }}>
                            {post.keepOnTopHours || '-'}/{post.autoDeleteHours || '-'}ч
                        </Text>
                    </Flex>
                    <Row gutter={13}>
                        <Col span={14}>
                            <div className={styles.paymentStatus}>
                                <Text style={{ color: isPaid ? colorSuccess : '#FBBE44' }}>
                                    {isPaid ? 'Оплачено' : 'Не оплачено'}
                                </Text>
                                <Text style={{ color: colorError }}>
                                    <WalletOutlined style={{ color: colorError }} />{' '}
                                    {post.advertisingInfo?.budget || '-'} Р
                                </Text>
                            </div>
                        </Col>
                        <Col span={10}>
                            <div className={styles.paymentMethod}>
                                <Text style={{ color: colorPrimary }}>{isFix ? 'Фикс' : 'CPM'}</Text>
                            </div>
                        </Col>
                    </Row>
                </Flex>
            </Flex>
            {collapsed && (
                <>
                    <Row gutter={[8, 16]}>
                        <Col span={24}>
                            <Text className={classNames(styles.partnerLabel)}>Партнер</Text>
                            <div className={styles.partner}>{post.advertisingInfo?.partner.name}</div>
                        </Col>
                        <Col span={12}>
                            <Text className={classNames(styles.indicatorLabel)}>Просмотры</Text>
                            <div className={classNames(styles.indicatorCard, { [styles.success]: true })}>
                                {post.advertisingInfo?.statistics.clicks}
                            </div>
                        </Col>
                        <Col span={12}>
                            <Text className={classNames(styles.indicatorLabel)}>Отписки</Text>
                            <div className={classNames(styles.indicatorCard, { [styles.error]: true })}>
                                {post.advertisingInfo?.statistics.leaves}
                            </div>
                        </Col>
                        <Col span={12}>
                            <Text className={classNames(styles.indicatorLabel)}>Подписчики</Text>
                            <div className={classNames(styles.indicatorCard, { [styles.success]: true })}>
                                {post.advertisingInfo?.statistics.joins}
                            </div>
                        </Col>
                        <Col span={12}>
                            <Text className={classNames(styles.indicatorLabel)}>Премиум</Text>
                            <div className={classNames(styles.indicatorCard, { [styles.success]: true })}>-</div>
                        </Col>
                        <Col span={12}>
                            <Text className={classNames(styles.indicatorLabel)}>CPS Общий</Text>
                            <div className={classNames(styles.indicatorCard, { [styles.neutral]: true })}>
                                {cpsCommmon ? `${cpsCommmon} Р` : '-'}
                            </div>
                        </Col>
                        <Col span={12}>
                            <Text className={classNames(styles.indicatorLabel)}>CPS Чистый</Text>
                            <div className={classNames(styles.indicatorCard, { [styles.neutral]: true })}>
                                {cpsPure ? `${cpsPure} Р` : '-'}
                            </div>
                        </Col>
                        <Col span={24}>
                            <Flex gap={8}>
                                <Button
                                    type="primary"
                                    size="large"
                                    style={{ width: '100%' }}
                                    onClick={onShareStatistics}
                                >
                                    Поделиться результатом <Share size={16} />
                                </Button>
                                <Dropdown
                                    key="more"
                                    trigger={['click']}
                                    menu={{
                                        items: moreActionsItems
                                    }}
                                >
                                    <Button style={{ verticalAlign: 'bottom' }} size="large">
                                        <MoreOutlined />
                                    </Button>
                                </Dropdown>
                            </Flex>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    )
}
